import styled, { css } from 'styled-components';
import type { FunctionComponent } from 'react';
import {
  paletteHelper,
  spacingHelper,
  typographyHelper,
  mediaHelper,
  radiusHelper,
  boxShadow,
  Tooltip,
  BadgeVariant,
  BadgeWithStatus,
} from '@property.com.au/ui-kit';
import { PropertyTimelineMedia } from '../PropertyTimelineMedia/PropertyTimelineMedia';
import { PropertyTimelineAgent } from '../PropertyTimelineAgent/PropertyTimelineAgent';
import {
  AgencyDetails,
  PropertyTimelineAgency,
} from '../PropertyTimelineAgency/PropertyTimelineAgency';
import { ScreenReaderTitle } from '../../../../../app-kit/helpers/screenReaderOnly';
import { WebLink, WebLinkProps } from '../../../../../app-kit/components/WebLink/WebLink';
import { LineClamp } from '../../../../../app-kit/helpers/LineClamp.styled';
import { MediaCount } from '../PropertyTimelineMediaCounts/PropertyTimelineMediaCounts';

const TimelineEventWrapper = styled.article``;

const EventHeadline = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  margin-bottom: ${spacingHelper('small')};
  ${mediaHelper('mobile')} {
    row-gap: ${spacingHelper('small')};
  }
`;

const EventTitle = styled.div`
  ${typographyHelper('body02Bold')};
`;

const EventSubtitle = styled.div`
  ${typographyHelper('body02Bold')};
`;

const EventDetails = styled(LineClamp)`
  ${typographyHelper('body02')};
  color: ${paletteHelper('textSecondary')};

  & + * {
    margin-top: ${spacingHelper('small')};
  }
`;

const StyledLink = styled(WebLink)<{ $addMarginBottom: boolean }>`
  margin: ${spacingHelper('0 extraSmall 0 0')};
  ${({ $addMarginBottom }) =>
    $addMarginBottom &&
    css`
      margin-bottom: ${spacingHelper('extraSmall')};
    `}
  ${mediaHelper('mobile')} {
    width: 100%;
  }
`;

const EventLinksWrapper = styled.div<{ $addPaddingBottom: boolean }>`
  display: flex;
  flex-flow: wrap row;
  padding-bottom: ${({ $addPaddingBottom }) => ($addPaddingBottom ? spacingHelper('small') : '0')};

  ${mediaHelper('mobile')} {
    flex-direction: column;
  }
`;

const AgentWrapper = styled.div`
  ${boxShadow.defaultBoxShadow};
  background: ${paletteHelper('backgroundPrimaryHigh')};
  padding: ${spacingHelper('medium')};
  border-radius: ${radiusHelper('medium')};
  margin-bottom: ${spacingHelper('small')};

  > * {
    width: 100%;
    &:not(:first-child) {
      margin-top: ${spacingHelper('small')};
    }
  }

  ${mediaHelper('tabletMin')} {
    display: inline-flex;
    flex-direction: row;
    align-items: center;
    > :not(:first-child) {
      margin-top: 0;
      margin-left: ${spacingHelper('medium')};
    }
    > ${AgencyDetails} {
      flex-shrink: 0;
      width: 192px;
    }
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const StyledTooltip = styled(Tooltip)`
  > * {
    background-color: transparent;
    color: ${paletteHelper('textSecondary')};
  }
`;

const BadgeWithMargin = styled(BadgeWithStatus)`
  margin-right: ${spacingHelper('medium')};
`;

interface TimelineImage {
  alt: string;
  jpgUri: string;
  webpUri: string;
}

interface TimelineMedia {
  image: TimelineImage;
  mediaCounts: MediaCount[];
  caption?: string | null;
  key?: string;
}

interface Agency {
  logo?: TimelineImage | null;
  brandColour: string;
}

interface Agent {
  name: string;
  jobTitle?: string | null;
  photo?: TimelineImage | null;
}

interface TimelineBadge {
  text: string;
  variant: BadgeVariant;
}

export interface PropertyTimelineEventProps {
  label: string;
  badge?: TimelineBadge | null;
  title?: string | null;
  tooltip?: string | null;
  subtitle?: string | null;
  details?: string | null;
  agent?: Agent | null;
  agency?: Agency | null;
  media?: TimelineMedia | null;
  links?: WebLinkProps[] | null;
}

export const PropertyTimelineEvent: FunctionComponent<PropertyTimelineEventProps> = ({
  label,
  badge,
  title,
  subtitle,
  details,
  agent,
  agency,
  media,
  links,
  tooltip,
}) => {
  return (
    <TimelineEventWrapper>
      <ScreenReaderTitle as="h3">
        {label} {badge && badge.text} {title && title}
      </ScreenReaderTitle>
      {(badge || title) && (
        <EventHeadline>
          {badge && <BadgeWithMargin variant={badge.variant}>{badge.text}</BadgeWithMargin>}
          <TitleWrapper>
            {title && <EventTitle>{title}</EventTitle>}
            {tooltip && (
              <StyledTooltip variant="question" placement="bottom-end">
                {tooltip}
              </StyledTooltip>
            )}
          </TitleWrapper>
        </EventHeadline>
      )}
      {subtitle && <EventSubtitle>{subtitle}</EventSubtitle>}
      {Boolean(agent || agency) && (
        <AgentWrapper>
          {agent && (
            <PropertyTimelineAgent
              name={agent.name}
              jobTitle={agent.jobTitle}
              photo={agent.photo}
            />
          )}
          {agency && <PropertyTimelineAgency brandColour={agency.brandColour} logo={agency.logo} />}
        </AgentWrapper>
      )}
      {details && <EventDetails $numLinesToClamp={2}>{details}</EventDetails>}
      {links && links.length > 0 && (
        <EventLinksWrapper $addPaddingBottom={Boolean(media)}>
          {links.map((link) => (
            <StyledLink
              $addMarginBottom={Boolean(media)}
              key={link.key}
              url={link.url}
              variant={link.variant}
              iconInfo={link.iconInfo}
              isExternal={link.isExternal}
              title={link.title}
              tracking={link.tracking}
            />
          ))}
        </EventLinksWrapper>
      )}
      {media && (
        <PropertyTimelineMedia
          galleryItemKey={media.key}
          image={media.image}
          counts={media.mediaCounts}
          caption={media.caption}
        />
      )}
    </TimelineEventWrapper>
  );
};
